"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.addRecords = void 0;
const command_manager_1 = require("../../command_manager");
const model_1 = require("../../model");
const resource_1 = require("../../modules/database/store/selectors/resource");
const utils_1 = require("../../utils");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const i18n_1 = require("../../exports/i18n");
const config_1 = require("../../config");
const MAX_RECORD_NUM = 50000;
exports.addRecords = {
    undoable: true,
    execute: (context, options) => {
        const { model: state, ldcMaintainer, memberFieldMaintainer, fieldMapSnapshot } = context;
        const { viewId, index, count, groupCellValues, cellValues, ignoreFieldPermission } = options;
        const datasheetId = options.datasheetId || store_1.Selectors.getActiveDatasheetId(state);
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        const fieldPermissionMap = store_1.Selectors.getFieldPermissionMap(state, datasheetId);
        const loading = (0, resource_1.getDatasheetLoading)(state, datasheetId);
        if (loading) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.datasheet_is_loading));
        }
        if (!snapshot) {
            return null;
        }
        if (count <= 0 || isNaN(count)) {
            return null;
        }
        if (cellValues && cellValues.length !== count) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.error_add_row_failed_wrong_length_of_value));
        }
        const recordIds = Object.keys(snapshot.recordMap);
        const newRecordIds = (0, utils_1.getNewIds)(utils_1.IDPrefix.Record, count, recordIds.length ? recordIds : snapshot.meta.views[0].rows.map(item => item.recordId));
        if ((recordIds.length + newRecordIds.length) > MAX_RECORD_NUM) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.max_record_num_per_dst));
        }
        const linkFieldIds = [];
        const specialActions = [];
        const fieldMap = snapshot.meta.fieldMap;
        for (const fieldId in fieldMap) {
            const field = fieldMap[fieldId];
            if (field.type === types_1.FieldType.Link && field.property.brotherFieldId) {
                linkFieldIds.push(field);
            }
            if (field.type === types_1.FieldType.CreatedBy) {
                const uuids = field.property.uuids;
                const uuid = state.user.info && state.user.info['uuid'];
                if (uuid && !uuids.includes(uuid)) {
                    const newField = Object.assign(Object.assign({}, field), { property: Object.assign(Object.assign({}, field.property), { uuids: [...uuids, uuid] }) });
                    const action = model_1.DatasheetActions.setFieldAttr2Action(snapshot, { field: newField });
                    action && specialActions.push(action);
                }
            }
        }
        const memberFieldMap = {};
        /**
         * Add a new record, the record may be a blank record, or there may be some initialized data,
         * The data of the initialized data has three parts:
         * 1. Copy a record, the original data in the target record
         * 2. There is a filter item, if the filter value is a certain value, the filter item will be included
         * 3. There is a group item, adding a record in a group will bring the data of the group
         * Assuming that a record is added, and the above three parts have corresponding data,
         * the weights will decrease in turn, that is, for the same field,
         * the data from the next-level source will be overwritten by the data from the previous-level source.
         */
        const actions = newRecordIds.reduce((collected, recordId, i) => {
            const userInfo = state.user.info;
            const newRecord = model_1.DatasheetActions.getDefaultNewRecord(state, snapshot, recordId, viewId, groupCellValues, userInfo);
            if (cellValues) {
                newRecord.data = Object.assign({}, newRecord.data, cellValues[i]);
            }
            /**
             * Add a new record, which may be substituted into the initial value due to filtering, grouping, and copying a row.
             * If permission is set for one of the columns, and the current user does not have editing permission,
             * the data of the corresponding column needs to be filtered out when setting the data.
             * Because all data will pass here after processing, unified filtering is performed here
             */
            if (fieldPermissionMap && !ignoreFieldPermission) {
                const _data = {};
                for (const fieldId in newRecord.data) {
                    const fieldRole = store_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, fieldId);
                    if (!fieldRole || fieldRole === config_1.ConfigConstant.Role.Editor) {
                        _data[fieldId] = newRecord.data[fieldId];
                    }
                }
                newRecord.data = _data;
            }
            /**
             * If there is data in the member field in the added record, special processing is required.
             * In the current logic, the member field header will record the unitId of all members in the current column after deduplication,
             * Therefore, when a new record is added and there is initialization data,
             * and the content of the member field exists in the data,
             * it is necessary to check whether the newly added unitId exists in the header.
             * If it does not exist, the data of the member header needs to be updated synchronously
             */
            if (newRecord.data) {
                const _recordData = {};
                for (const [fieldId, cellValue] of Object.entries(newRecord.data)) {
                    if (!fieldMap[fieldId]) {
                        // Compatible processing for data exceptions, some tables in the template center have dirty data
                        continue;
                    }
                    // There will be column data that does not exist in the row record. After the middle layer strengthens the verification,
                    // this part of the data will cause an error, so only the column data that still exists in the fieldId is retained here.
                    _recordData[fieldId] = cellValue;
                    fieldMapSnapshot[fieldId] = fieldMap[fieldId];
                    if (fieldMap[fieldId].type !== types_1.FieldType.Member) {
                        continue;
                    }
                    const unitIds = Array.isArray(cellValue) ? cellValue : [];
                    if (!memberFieldMap[fieldId]) {
                        memberFieldMap[fieldId] = [...unitIds];
                        continue;
                    }
                    memberFieldMap[fieldId].push(...unitIds);
                }
                newRecord.data = _recordData;
            }
            const action = model_1.DatasheetActions.addRecord2Action(snapshot, {
                viewId,
                record: newRecord,
                index: index + i,
            });
            if (!action) {
                return collected;
            }
            linkFieldIds.forEach((field) => {
                const value = newRecord.data[field.id];
                const linkedSnapshot = store_1.Selectors.getSnapshot(state, field.property.foreignDatasheetId);
                // When the associated field cell itself has no value, do nothing
                if (!value) {
                    return;
                }
                ldcMaintainer.insert(state, linkedSnapshot, newRecord.id, field, value, null);
            });
            collected.push(...action);
            return collected;
        }, []);
        for (const [fieldId, cellValueForUnitIds] of Object.entries(memberFieldMap)) {
            const field = fieldMap[fieldId];
            const unitIds = field.property.unitIds || [];
            const _unitIds = [...new Set([...unitIds, ...cellValueForUnitIds])];
            if (_unitIds.length === unitIds.length) {
                continue;
            }
            memberFieldMaintainer.insert(fieldId, _unitIds, datasheetId);
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            data: newRecordIds,
            actions: [...actions, ...specialActions],
            fieldMapSnapshot,
        };
    },
};
/*

 declare module '../../command_manager/command_manager' {
 interface CollaCommandManager {
 execute(options: IAddRecordsOptions & { cmd: 'AddRecords' }): ICollaCommandExecuteResult<IAddRecordsResult>;
 }
 }

 */
//# sourceMappingURL=add_records.js.map